import {isString} from 'lodash';
import React, {FC, ReactNode, useEffect, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ConversationSortPropertiesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  CellsPanelId,
  findInboxesPanelIdInPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../../../helpers/imageThemeHelpers';
import {InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {getBroaderSearchLocations} from '../../../../store/panels/cells/search/cellsPanelSearchHelpers';
import {
  getCellsSearchQueryHash,
  getCellsSearchResultsCounts,
} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {toggleSearchQueryBuilderDropdownAction} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {
  InboxesConversationsParametersWithSearch,
  InboxesSearchParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {SearchInteractionOptions} from '../../../../store/stateless/searchActions';
import {makeGetSearchLocalFilterName} from '../hints/searchHintsBar';
import {trackSearchEmptyStateView, trackSearchNoResultsNudgeClick} from '../searchTrackingHelpers';
import {SearchNoResultsHeader} from './searchNoResultsHeader';

/*
 * Constants.
 */

export const SEARCH_NO_RESULTS_MAX_CHARS_QUERY = 20;
const MAX_CHARS_LOCATION = 40;

/*
 * Props.
 */

export interface SearchNoResultsMessageProps {
  panelId: CellsPanelId;
  parameters: InboxesConversationsParametersWithSearch;
  cellsContext: CellsContext;
  navigateToSearch: (
    searchParameters: InboxesSearchParameters,
    cellsContext?: CellsContext,
    options?: SearchInteractionOptions,
    sortProperty?: ConversationSortPropertiesEnum,
  ) => void;
}

/*
 * Styles.
 */

export const StyledNoResultsWrapperDiv = styled.div`
  max-width: 480px;
`;

export const StyledNoResultsFooterDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StyledNoResultsFooterContentDiv = styled.div`
  margin-top: 8px;
  margin-bottom: 16px;

  white-space: normal;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: center;
  line-height: 21px;
`;

/*
 * Component.
 */

export const SearchNoResultsMessage: FC<SearchNoResultsMessageProps> = (props) => {
  const {panelId, parameters, cellsContext, navigateToSearch} = props;
  const {
    search: {query, location: currentSearchLocation},
  } = parameters;

  const locationSuggestion = useSuggestedSearchLocation(parameters);

  const getSearchLocalFilterName = useMemo(() => makeGetSearchLocalFilterName(parameters), [parameters]);
  const localSearchFilterName = useAppSelector(getSearchLocalFilterName);

  const queryHash = useAppSelector(getCellsSearchQueryHash);
  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  const inboxesPanelId = findInboxesPanelIdInPanelId(panelId);
  const toggleSearchQueryBuilderDropdown = () =>
    // Toggle query builder
    dispatch(toggleSearchQueryBuilderDropdownAction(inboxesPanelId, 'no-results-helpful-tip'));

  useEffect(() => {
    if (!queryHash) {
      return;
    }

    const variant = isDefined(locationSuggestion) ? 'other_location' : 'no_results';
    trackSearchEmptyStateView({queryHash, variant});
  }, [locationSuggestion, queryHash]);

  const searchNoResultsAnywhere = findThemedImageInfo(theme, ThemedImageNamesEnum.SEARCH_NO_RESULTS_ANYWHERE);

  // Render a helpful tip if we're sure we won't find any results.
  if (!locationSuggestion) {
    return (
      <StyledNoResultsWrapperDiv>
        <SearchNoResultsHeader
          image={searchNoResultsAnywhere.src}
          imageWidth={searchNoResultsAnywhere.width}
          imageHeight={searchNoResultsAnywhere.height}
        >
          <Trans id="search-no-results-helpful-tip-header">
            Didn&apos;t find {maybeTruncateAndAddQuotes(query, SEARCH_NO_RESULTS_MAX_CHARS_QUERY, true)}{' '}
            anywhere
          </Trans>
        </SearchNoResultsHeader>
        <StyledNoResultsFooterDiv>
          <StyledNoResultsFooterContentDiv>
            <Trans id="search-no-results-helpful-tip-footer">
              Use the{' '}
              <InlineLinkButton
                interactionId="search-no-results-helpful-tip-cta"
                onClick={toggleSearchQueryBuilderDropdown}
              >
                search builder
              </InlineLinkButton>{' '}
              to try another search.
            </Trans>
          </StyledNoResultsFooterContentDiv>
        </StyledNoResultsFooterDiv>
      </StyledNoResultsWrapperDiv>
    );
  }

  const headerTitle = (
    <Trans id="search-no-results-nudge-header">
      Didn&apos;t find {maybeTruncateAndAddQuotes(query, SEARCH_NO_RESULTS_MAX_CHARS_QUERY, true)} in{' '}
      {renderLocationLabel(currentSearchLocation, localSearchFilterName)}
    </Trans>
  );

  const renderFooter = () => {
    const onCtaClick = () => {
      // Track.
      trackSearchNoResultsNudgeClick({
        queryHash,
        currentLocation: parameters.search.location,
        suggestedLocation: locationSuggestion,
      });

      // Navigate.
      navigateToSearch({...parameters.search, location: locationSuggestion}, cellsContext, {
        isFolderSwitch: true,
      });
    };

    const footerText =
      locationSuggestion === InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE ? (
        <Trans id="search-no-results-nudge-footer-sidebar">
          Searching your sidebar, we found matching results.
        </Trans>
      ) : (
        <Trans id="search-no-results-nudge-footer-global">
          Searching all inboxes, we found matching results.
        </Trans>
      );

    return (
      <StyledNoResultsFooterDiv>
        <StyledNoResultsFooterContentDiv>{footerText}</StyledNoResultsFooterContentDiv>
        <Button
          interactionId="search-no-results-nudge-cta"
          onClick={onCtaClick}
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
        >
          <Trans id="search-no-results-nudge-cta">View results</Trans>
        </Button>
      </StyledNoResultsFooterDiv>
    );
  };

  return (
    <StyledNoResultsWrapperDiv>
      <SearchNoResultsHeader>{headerTitle}</SearchNoResultsHeader>
      {renderFooter()}
    </StyledNoResultsWrapperDiv>
  );
};

/*
 * Helpers.
 */

function useSuggestedSearchLocation(parameters: InboxesConversationsParametersWithSearch) {
  const cellsSearchResultsCounts = useAppSelector(getCellsSearchResultsCounts);

  // Compute locations for which we need to check if results exist.
  const broaderLocations = getBroaderSearchLocations(parameters.search.location);

  // Take the first broader location that has results.
  return broaderLocations.find((location) => {
    const totalForLocation = cellsSearchResultsCounts[location];
    return isDefined(totalForLocation) && totalForLocation > 0;
  });
}

export function maybeTruncateAndAddQuotes(
  value: string,
  maxLength: number,
  shouldAddQuotes?: boolean,
): ReactNode {
  if (!value) {
    return null;
  }

  if (value.length <= maxLength) {
    return maybeAddQuotes(value, shouldAddQuotes);
  }

  return (
    <TooltipCoordinator isInline render={() => maybeAddQuotes(value, shouldAddQuotes)}>
      {maybeAddQuotes(`${value.slice(0, maxLength)}…`, shouldAddQuotes)}
    </TooltipCoordinator>
  );
}

function maybeAddQuotes(value: string, shouldAddQuotes?: boolean) {
  if (!shouldAddQuotes) {
    return value;
  }

  return `“${value}“`;
}

function renderLocationLabel(
  location: InboxesSearchLocationsEnum,
  localSearchFilterName?: ReactNode,
): ReactNode {
  switch (location) {
    case InboxesSearchLocationsEnum.LOCAL:
      return isString(localSearchFilterName)
        ? maybeTruncateAndAddQuotes(localSearchFilterName, MAX_CHARS_LOCATION)
        : localSearchFilterName;

    case InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE:
      return <Trans id="search-no-results-your-sidebar">your sidebar</Trans>;

    default:
      return null;
  }
}
